import { STATIC_STRINGS } from "@asez/shared";

export const isNull = (value: any): boolean => {
  return value === null;
};

export const isUndefined = (value: any): boolean => {
  return value === undefined;
};

export const isEmptyObject = (value: any): boolean => {
  return (
    isUndefined(value) ||
    isNull(value) ||
    (typeof value === "object" && Object.keys(value).length === 0)
  );
};

export const isEmpty = (value) => {
  return (
    value === undefined ||
    value === null ||
    (typeof value === "object" && Object.keys(value).length === 0) ||
    (typeof value === "string" && value.trim().length === 0) ||
    (Array.isArray(value) && value.length === 0)
  );
};

export const formatDateTime = (isoString: string) => {
  const date = new Date(isoString);

  // Extract date parts
  const day = date.getDate();
  const year = date.getFullYear();
  const monthNames = STATIC_STRINGS.MONTHS;
  const month = monthNames[date.getMonth()];

  // Extract time parts
  let hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12; // Convert 24-hour format to 12-hour format

  return `${day}th ${month} ${hours}:${minutes} ${ampm}`;
};

export const getStartTime = () => {
  const now = new Date();
  return now.toISOString().slice(0, 10) + 'T' + now.toTimeString().slice(0, 5);
};

export const calculateEndTime = (startTime: string, durationInHours = 1) => {
  const startDate = new Date(startTime);
  const timezoneOffset = new Date().getTimezoneOffset() * 60000;
  const endTime = new Date(
    startDate.getTime() + durationInHours * 60 * 60 * 1000 - timezoneOffset
  );
  return endTime.toISOString().slice(0, 16);
};

export const formatPercentage = (value, decimalPlaces) => {
  if (typeof value !== "number" || typeof decimalPlaces !== "number") {
    throw new Error("Both arguments must be numbers");
  }

  return `${(value * 100).toFixed(decimalPlaces)}`;
};

export const convertToCSV = (
  data: Record<string, any>[],
  visibleColumns: { key: string; label: string }[]
): string => {
  if (!data || data.length === 0) {
    console.warn("No data available for CSV export.");
    return "";
  }

  const mandatoryFields = [
    { key: "name", label: "Name" },
    { key: "email", label: "Email" },
    { key: "feedback", label: "Feedback" },
    { key: "stage", label: "Stage" },
    { key: "assessmentPdfLink", label: "Assessment Report" },
  ];

  const finalColumns = [
    ...mandatoryFields,
    ...visibleColumns.filter(
      (col) => !mandatoryFields.some((mf) => mf.key === col.key)
    ),
  ];

  const headers = finalColumns.map((col) => col.label).join(",") + "\n";

  // Create CSV rows
  const rows = data.map((row) => {
    return finalColumns
      .map(({ key, label }) => {
        let value = row[key];

        if (value === undefined) {
          const formField = row.formFields?.find(
            (field: any) => field.id === key || field.label === label
          );
          value = formField ? formField.value : "";
        }

        // Handle arrays
        if (Array.isArray(value)) {
          value = value.join(", ");
        }

        // Escape CSV characters
        if (typeof value === "string") {
          value = `"${value.replace(/"/g, '""')}"`;
        }

        return value ?? "";
      })
      .join(",");
  });

  return headers + rows.join("\n");
};

const columnKeyMappings: Record<string, string> = {
  TotalMarks: "scorePercentage",
};

export const handleDownloadCSV = (data: Record<string, any>[], visibleColumns: any) => {
  if (!data || data.length === 0) {
    console.warn("No data available for download.");
    return;
  }

  const mappedColumns = visibleColumns.map((col) => ({
    key: columnKeyMappings[col.key] || col.key, // Use mapped key if exists, otherwise keep original
    label: col.label,
  }));

  const csvContent = convertToCSV(data, mappedColumns);
  if (!csvContent) return;

  const blob = new Blob([csvContent], { type: "text/csv" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.download = "report.csv";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const getAssessmentDuration = (duration) => {

  const hours = Math.floor(duration / (60 * 60));
  const minutes = Math.floor((duration/60) % 60);
  const seconds = (duration % 60);

  return `${hours}:${minutes}:${seconds}`;
};

export const csvToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
  });
};

export const copyToClipboard = (text: string) => {
  navigator.clipboard.writeText(text);
};
export const getFileSize = (size: number) => {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)} KB`;
  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
};
